/**
 * Carrega o detalhe de um trânsito, e sabe recarregar depois de uma ação.
 *
 * O `recarregar` existe porque enviar ou consultar muda o trânsito do lado do servidor —
 * status, protocolo, prazos, histórico. Reconstruir esse estado no cliente a partir da
 * resposta da ação seria manter duas verdades sobre a mesma coisa, e a do cliente ficaria
 * errada no dia em que a API passasse a mudar mais um campo.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import { imagemDaAnalise } from "../api/tipos";
import type {
  DetalheDoTransito,
  EventoDaIntegracao,
  ResultadoDaCapacidade,
} from "../api/tipos";
import { EVENTO_DO_RELOGIO } from "../relogio";

export interface EstadoDoTransito {
  detalhe: DetalheDoTransito | null;
  trilha: EventoDaIntegracao[];
  /** O que as IAs do módulo de Análise disseram, quando o trânsito nasceu de upload. */
  analise: ResultadoDaCapacidade[];
  /** O que as IAs disseram sobre **cada vista** do trânsito agrupado, pela imagem de cada uma
   *  (03/11): é o que separa "placa ilegível" de "o serviço de placa não respondeu". */
  analiseDasVistas: Record<string, ResultadoDaCapacidade[]>;
  carregando: boolean;
  erro: ErroDaApi | null;
  recarregar: () => Promise<void>;
}

export function useTransito(
  id: string | null,
  /** O que as IAs da Análise leram no upload. O regulador não o lê (`OB-13`): pedir seria uma
   *  recusa na trilha dele a cada trânsito aberto. */
  comAnalise = true,
): EstadoDoTransito {
  const [detalhe, definirDetalhe] = useState<DetalheDoTransito | null>(null);
  const [trilha, definirTrilha] = useState<EventoDaIntegracao[]>([]);
  const [analise, definirAnalise] = useState<ResultadoDaCapacidade[]>([]);
  const [analiseDasVistas, definirAnaliseDasVistas] = useState<
    Record<string, ResultadoDaCapacidade[]>
  >({});
  const [carregando, definirCarregando] = useState(false);
  const [erro, definirErro] = useState<ErroDaApi | null>(null);
  // Só a resposta do **último** pedido vale (tarefa 20), como na fila (29/09). Clicar num
  // veículo e logo em outro deixava os dois pedidos no ar; se o do primeiro chegasse por
  // último, a Conferência mostrava o primeiro com o segundo marcado na lista — o operador
  // decidindo sobre um registro achando que era outro.
  const ultimo = useRef(0);

  const buscar = useCallback(async () => {
    const pedido = ++ultimo.current;
    if (!id) {
      definirDetalhe(null);
      definirTrilha([]);
      definirAnalise([]);
      definirAnaliseDasVistas({});
      definirErro(null);
      definirCarregando(false);
      return;
    }
    // Outro veículo: o painel do anterior sai já, e não quando o novo chegar. Enquanto o
    // pedido estava no ar, a Conferência mostrava o anterior com os botões ativos e o novo
    // marcado na lista — e o atalho A aprovaria o anterior. Recarregar o mesmo veículo
    // (depois de uma ação) mantém o painel, sem piscar.
    definirDetalhe((atual) => (atual && atual.id !== id ? null : atual));
    definirCarregando(true);
    try {
      // As duas em paralelo: são independentes, e a trilha costuma estar vazia — esperar
      // uma pela outra só atrasaria a tela sem simplificar nada.
      const [d, t] = await Promise.all([
        api<DetalheDoTransito>(`/tollvision/transitos/${id}`),
        api<EventoDaIntegracao[]>(`/tollvision/transitos/${id}/integracao`),
      ]);
      // Só depois do detalhe: é ele que diz se há imagem de análise por trás. Falhar aqui
      // (módulo de Análise não licenciado, imagem expurgada) não derruba o trânsito — o
      // painel diz "sem leitura", que é o que aconteceu.
      const imagem = comAnalise ? imagemDaAnalise(d) : null;
      const leituras = imagem
        ? await api<ResultadoDaCapacidade[]>(
            `/analise/imagens/${imagem}/resultados`,
          ).catch(() => [])
        : [];
      // As vistas do upload agrupado: uma consulta por imagem, em paralelo. Falhar em uma
      // (ou em todas) só deixa a tela sem o diagnóstico — nunca derruba o trânsito.
      const porVista: Record<string, ResultadoDaCapacidade[]> = {};
      if (comAnalise) {
        await Promise.all(
          (d.imagens_de_analise ?? []).map(async (v) => {
            porVista[v.imagem_id] = await api<ResultadoDaCapacidade[]>(
              `/analise/imagens/${v.imagem_id}/resultados`,
            ).catch(() => []);
          }),
        );
      }
      if (pedido !== ultimo.current) return;
      definirDetalhe(d);
      definirTrilha(t);
      definirAnalise(leituras);
      definirAnaliseDasVistas(porVista);
      definirErro(null);
    } catch (falha) {
      if (pedido !== ultimo.current) return;
      // A prévia em HTML não tratava erro aqui, e o sintoma era a tela congelar no trânsito
      // anterior — o operador lendo dados de um registro e achando que eram de outro.
      definirErro(
        falha instanceof ErroDaApi
          ? falha
          : new ErroDaApi("Falha inesperada.", 0),
      );
      definirDetalhe(null);
      definirTrilha([]);
      definirAnalise([]);
      definirAnaliseDasVistas({});
    } finally {
      if (pedido === ultimo.current) definirCarregando(false);
    }
  }, [id, comAnalise]);

  useEffect(() => {
    void buscar();
  }, [buscar]);

  // O relógio da demonstração andou (ADR-059): relê agora e de novo daqui a pouco — o
  // worker, acordado pela API, vence nesse intervalo o que venceu no tempo novo (o aviso da
  // CNH, o cancelamento dos 7 dias), e o painel aberto mostra sem ninguém clicar.
  useEffect(() => {
    let espera: number | undefined;
    const andar = () => {
      void buscar();
      window.clearTimeout(espera);
      espera = window.setTimeout(() => void buscar(), 5_000);
    };
    window.addEventListener(EVENTO_DO_RELOGIO, andar);
    return () => {
      window.removeEventListener(EVENTO_DO_RELOGIO, andar);
      window.clearTimeout(espera);
    };
  }, [buscar]);

  return {
    detalhe,
    trilha,
    analise,
    analiseDasVistas,
    carregando,
    erro,
    recarregar: buscar,
  };
}
